import React, { createContext, useState, PropsWithChildren, useEffect, useMemo, useCallback, useRef } from "react";
import { formatDistanceToNow, parseISO, isValid } from "date-fns";
import { toZonedTime, fromZonedTime, format as formatTz } from "date-fns-tz";
import useAuth from "@/hooks/useAuth";
import { profileService } from "@/services/profileService";
import type { DateTimeSettings, DateTimeContextProps } from "@/types/providers/dateTime";

const defaultSettings: DateTimeSettings = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  dateFormat: "MM/dd/yyyy",
  timeFormat: "12",
};

export const DateTimeContext = createContext<DateTimeContextProps>({
  settings: defaultSettings,
  loading: true,
  formatTimestamp: () => "",
  formatDate: () => "",
  formatTime: () => "",
  formatDuration: () => "",
  formatRelative: () => "",
  toUserTimezone: (date) => new Date(date),
  fromUserTimezone: (date) => date,
  isToday: () => false,
  isPast: () => false,
  updateSettings: () => {},
  datePlaceholder: "MM/DD/YYYY",
  datetimePlaceholder: "MM/DD/YYYY h:mm AM",
});

export function DateTimeProvider({ children }: PropsWithChildren) {
  const { user, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState<DateTimeSettings>(defaultSettings);
  const [loading, setLoading] = useState(authLoading);

  useEffect(() => {
    const loadUserSettings = async () => {
      if (!user?.id) {
        setSettings(defaultSettings);
        setLoading(false);
        return;
      }

      try {
        const profile = await profileService.getProfile(user.id);

        const userSettings: DateTimeSettings = {
          timezone: profile.meta_data?.timezone || defaultSettings.timezone,
          dateFormat: profile.meta_data?.format || defaultSettings.dateFormat,
          timeFormat: (profile.meta_data?.time as "12" | "24") || defaultSettings.timeFormat,
        };

        setSettings(userSettings);
      } catch (error) {
        console.error("Failed to load user datetime settings:", error);
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    };

    loadUserSettings();
  }, [user?.id]);

  const parseDate = useCallback((date: string | Date): Date => {
    if (date instanceof Date) return date;
    const parsed = parseISO(date);
    return isValid(parsed) ? parsed : new Date(date);
  }, []);

  const toUserTimezone = useCallback(
    (date: string | Date): Date => {
      const parsedDate = parseDate(date);
      return toZonedTime(parsedDate, settings.timezone);
    },
    [parseDate, settings.timezone],
  );

  const fromUserTimezone = useCallback(
    (date: Date): Date => {
      return fromZonedTime(date, settings.timezone);
    },
    [settings.timezone],
  );

  const updateSettings = useCallback((newSettings: DateTimeSettings): void => {
    setSettings(newSettings);
  }, []);

  const getTimeFormatString = useMemo((): string => {
    return settings.timeFormat === "24" ? "HH:mm" : "h:mm a";
  }, [settings.timeFormat]);

  // Human-readable placeholder formats
  const datePlaceholder = useMemo((): string => {
    // Convert date-fns format to human-readable placeholder
    return settings.dateFormat.toUpperCase().replace(/D/g, "D").replace(/Y/g, "Y");
  }, [settings.dateFormat]);

  const datetimePlaceholder = useMemo((): string => {
    const timePart = settings.timeFormat === "24" ? "HH:MM" : "h:mm AM";
    return `${datePlaceholder} ${timePart}`;
  }, [datePlaceholder, settings.timeFormat]);

  // Cache for formatted dates to avoid recalculating same dates
  const formatCache = useRef<Map<string, string>>(new Map());

  const formatTimestamp = useCallback(
    (timestamp: string | Date, includeTime: boolean = true): string => {
      const cacheKey = `${timestamp.toString()}-${includeTime}-${settings.dateFormat}-${settings.timeFormat}-${settings.timezone}`;

      if (formatCache.current.has(cacheKey)) {
        return formatCache.current.get(cacheKey)!;
      }

      try {
        const userDate = toUserTimezone(timestamp);
        const datePattern = settings.dateFormat;
        const timePattern = getTimeFormatString;
        const pattern = includeTime ? `${datePattern} ${timePattern}` : datePattern;

        const result = formatTz(userDate, pattern, { timeZone: settings.timezone });

        // Limit cache size to prevent memory leaks
        if (formatCache.current.size > 1000) {
          formatCache.current.clear();
        }
        formatCache.current.set(cacheKey, result);

        return result;
      } catch {
        return timestamp.toString();
      }
    },
    [toUserTimezone, settings.dateFormat, settings.timeFormat, settings.timezone, getTimeFormatString],
  );

  const formatDate = useCallback(
    (date: string | Date): string => {
      const cacheKey = `date-${date.toString()}-${settings.dateFormat}-${settings.timezone}`;

      if (formatCache.current.has(cacheKey)) {
        return formatCache.current.get(cacheKey)!;
      }

      try {
        const userDate = toUserTimezone(date);
        const result = formatTz(userDate, settings.dateFormat, { timeZone: settings.timezone });

        if (formatCache.current.size > 1000) {
          formatCache.current.clear();
        }
        formatCache.current.set(cacheKey, result);

        return result;
      } catch {
        return date.toString();
      }
    },
    [toUserTimezone, settings.dateFormat, settings.timezone],
  );

  const formatTime = useCallback(
    (time: string | Date): string => {
      const cacheKey = `time-${time.toString()}-${settings.timeFormat}-${settings.timezone}`;

      if (formatCache.current.has(cacheKey)) {
        return formatCache.current.get(cacheKey)!;
      }

      try {
        const userDate = toUserTimezone(time);
        const result = formatTz(userDate, getTimeFormatString, { timeZone: settings.timezone });

        if (formatCache.current.size > 1000) {
          formatCache.current.clear();
        }
        formatCache.current.set(cacheKey, result);

        return result;
      } catch {
        return time.toString();
      }
    },
    [toUserTimezone, settings.timeFormat, settings.timezone, getTimeFormatString],
  );

  const formatDuration = (start: string | Date, end: string | Date): string => {
    try {
      const startDate = parseDate(start);
      const endDate = parseDate(end);
      const hours = Math.round(((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60)) * 10) / 10;
      return `${hours} ${hours === 1 ? "hour" : "hours"}`;
    } catch {
      return "—";
    }
  };

  const formatRelative = (date: string | Date): string => {
    try {
      const userDate = toUserTimezone(date);
      return formatDistanceToNow(userDate, { addSuffix: true });
    } catch {
      return date.toString();
    }
  };

  const isToday = (date: string | Date): boolean => {
    try {
      const userDate = toUserTimezone(date);
      const now = toUserTimezone(new Date());

      return (
        userDate.getDate() === now.getDate() &&
        userDate.getMonth() === now.getMonth() &&
        userDate.getFullYear() === now.getFullYear()
      );
    } catch {
      return false;
    }
  };

  const isPast = (date: string | Date): boolean => {
    try {
      const userDate = toUserTimezone(date);
      const now = toUserTimezone(new Date());

      // Compare dates by setting time to midnight (start of day)
      const userDateMidnight = new Date(userDate);
      userDateMidnight.setHours(0, 0, 0, 0);

      const nowMidnight = new Date(now);
      nowMidnight.setHours(0, 0, 0, 0);

      return userDateMidnight < nowMidnight;
    } catch {
      return false;
    }
  };

  const contextValue: DateTimeContextProps = {
    settings,
    loading,
    formatTimestamp,
    formatDate,
    formatTime,
    formatDuration,
    formatRelative,
    toUserTimezone,
    fromUserTimezone,
    isToday,
    isPast,
    updateSettings,
    datePlaceholder,
    datetimePlaceholder,
  };

  return <DateTimeContext.Provider value={contextValue}>{children}</DateTimeContext.Provider>;
}
